import { P as PubSub } from './pubsub-CoPYzvVm.js';
import './notyf-DIw0RvBj.js';

const CLOSE_TRANSITION_FALLBACK_MS = 350;

/**
 * @class DialogHandoff
 * @extends HTMLElement
 * @description Closes a drawer dialog, waits for its close transition, then opens a target modal via PubSub.
 * @example
 * <dialog-handoff data-close-dialog="menu-drawer" data-open-dialog="localization-modal">
 *   <button type="button">Open localization</button>
 * </dialog-handoff>
 */
class DialogHandoff extends HTMLElement {
  #trigger = this.querySelector('button');
  #abort = new AbortController();
  /** @type {(() => void) | null} */
  #unsubscribeDialogOpen = null;
  /** @type {(() => void) | null} */
  #unsubscribeDialogClose = null;

  connectedCallback() {
    const { closeDialog, openDialog } = this.dataset;
    if (!closeDialog || !openDialog || !this.#trigger) return;

    this.#trigger.setAttribute('aria-controls', openDialog);
    this.#trigger.setAttribute('aria-haspopup', 'dialog');
    this.#trigger.setAttribute('aria-expanded', 'false');

    this.#trigger.addEventListener('click', (event) => this.#handleClick(event), {
      signal: this.#abort.signal,
    });

    this.#unsubscribeDialogOpen = PubSub.subscribe('dialog:open', ({ id }) => {
      if (id !== openDialog) return;
      this.#trigger.ariaExpanded = 'true';
    });

    this.#unsubscribeDialogClose = PubSub.subscribe('dialog:close', ({ id }) => {
      if (id !== openDialog) return;
      this.#trigger.ariaExpanded = 'false';
    });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#unsubscribeDialogOpen?.();
    this.#unsubscribeDialogClose?.();
    this.#unsubscribeDialogOpen = null;
    this.#unsubscribeDialogClose = null;
  }

  /**
   * @param {MouseEvent} event
   */
  async #handleClick(event) {
    event.preventDefault();

    const { closeDialog, openDialog } = this.dataset;
    if (!closeDialog || !openDialog) return;

    await this.#closeDialogAndWait(closeDialog);
    PubSub.publish('dialog:open', { id: openDialog });
  }

  /**
   * @param {string} id
   * @returns {Promise<void>}
   */
  #closeDialogAndWait(id) {
    const dialog = document.getElementById(id);
    if (!dialog || dialog.ariaHidden === 'true') return Promise.resolve();

    return new Promise((resolve) => {
      const body = dialog.querySelector('[data-body]');

      const done = () => {
        clearTimeout(fallback);
        body?.removeEventListener('transitionend', onTransitionEnd);
        resolve();
      };

      const onTransitionEnd = ({ target }) => {
        if (target !== body) return;
        done();
      };

      body?.addEventListener('transitionend', onTransitionEnd);
      const fallback = setTimeout(done, CLOSE_TRANSITION_FALLBACK_MS);

      PubSub.publish('dialog:close', { id });
    });
  }
}

customElements.define('dialog-handoff', DialogHandoff);
